import { useState, useEffect, useRef, useCallback } from "react";
import { useBootstrapData } from "@/lib/bootstrap";

interface UseAutoScrollOptions {
  scrollContainer?: HTMLElement | null;
}

export function useAutoScroll(options: UseAutoScrollOptions = {}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const accumulatedScrollRef = useRef<number>(0);
  const programmaticScrollRef = useRef(false);

  const { data: speed = 40 } = useBootstrapData((data) => data.interaction.autoScrollSpeed ?? 40);

  const stopScrolling = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const togglePlay = useCallback(() => {
    setIsPlaying(prev => {
      if (!prev) {
        accumulatedScrollRef.current = 0;
      }
      return !prev;
    });
  }, []);

  useEffect(() => {
    const isScrolledToBottom = () => {
      const el = options.scrollContainer;
      const isElementScrollable = el && el.scrollHeight > el.clientHeight;
      if (isElementScrollable) {
        return el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
      }
      return window.innerHeight + window.scrollY >= document.body.scrollHeight - 10;
    };

    const scroll = (timestamp: number) => {
      if (!isPlaying) {
        animationFrameRef.current = requestAnimationFrame(scroll);
        lastTimeRef.current = timestamp;
        return;
      }

      if (isScrolledToBottom()) {
        animationFrameRef.current = requestAnimationFrame(scroll);
        lastTimeRef.current = timestamp;
        return;
      }

      const deltaTime = lastTimeRef.current ? (timestamp - lastTimeRef.current) / 1000 : 0;
      lastTimeRef.current = timestamp;

      const scrollAmount = speed * deltaTime;

      if (scrollAmount > 0) {
        accumulatedScrollRef.current += scrollAmount;
        const pixelsToScroll = Math.floor(accumulatedScrollRef.current);
        if (pixelsToScroll >= 1) {
          accumulatedScrollRef.current -= pixelsToScroll;
          programmaticScrollRef.current = true;
          const el = options.scrollContainer;
          const isElementScrollable = el && el.scrollHeight > el.clientHeight;
          if (isElementScrollable) {
            el.scrollTop += pixelsToScroll;
          } else {
            window.scrollBy({ top: pixelsToScroll, behavior: "auto" });
          }
        }
      }

      animationFrameRef.current = requestAnimationFrame(scroll);
    };

    animationFrameRef.current = requestAnimationFrame(scroll);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, options.scrollContainer, speed]);

  useEffect(() => {
    if (!isPlaying) return;

    const scrollContainer = options.scrollContainer || window;

    const handleWheel = () => {
      setIsPlaying(false);
    };

    const handleTouchStart = () => {
      setIsPlaying(false);
    };

    const handleKeyDown = (e: Event) => {
      const ke = e as KeyboardEvent;
      const pauseKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
      if (pauseKeys.includes(ke.key)) {
        setIsPlaying(false);
      }
    };

    const handleDocumentClick = (e: Event) => {
      const target = e.target as HTMLElement;
      const eventCard = target.closest('[data-testid="compact-event-item"]');
      if (eventCard) {
        setIsPlaying(false);
      }
    };

    const handleScroll = () => {
      if (programmaticScrollRef.current) {
        programmaticScrollRef.current = false;
        return;
      }
      setIsPlaying(false);
    };

    const handleMouseDown = (e: Event) => {
      const target = e.target as HTMLElement;
      if (
        target.closest('[data-radix-scroll-area-viewport]') ||
        target.tagName === 'SCROLLBAR' ||
        target.classList.contains('scrollbar-thumb')
      ) {
        setIsPlaying(false);
      }
    };

    scrollContainer.addEventListener('wheel', handleWheel, { passive: true });
    scrollContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleDocumentClick, { capture: true });
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
      scrollContainer.removeEventListener('touchstart', handleTouchStart);
      scrollContainer.removeEventListener('scroll', handleScroll);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleDocumentClick, { capture: true });
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [options.scrollContainer, isPlaying]);

  return {
    isPlaying,
    togglePlay,
    stopScrolling,
  };
}
